import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type {
  GetTemplateCreationRecordResponse,
  UpdateTemplateVersionControlRequest,
} from '../../contracts';
import {
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
  UPDATE_VERSION_CONTROL_KEY,
} from '../../contracts';
import { updateTemplateVersionControl } from '../api/templates';

export const useMutateUpdateVersionControl = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [UPDATE_VERSION_CONTROL_KEY],
    mutationFn: async ({
      versionControlId,
      data,
    }: {
      versionControlId: string;
      data: UpdateTemplateVersionControlRequest;
      templateCreationRecordId: string;
    }) => {
      const { data: versionControl } = await updateTemplateVersionControl({
        versionControlId,
        request: data,
      });
      return versionControl;
    },
    onMutate: async ({ templateCreationRecordId, versionControlId, data }) => {
      await queryClient.cancelQueries({
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
      });

      const previousData = queryClient.getQueryData<GetTemplateCreationRecordResponse>([
        GET_TEMPLATE_CREATION_RECORD_BY_ID,
        { templateCreationRecordId },
      ]);

      queryClient.setQueryData<GetTemplateCreationRecordResponse>(
        [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        (oldData) => {
          if (!oldData) return oldData;

          return produce(oldData, (draft) => {
            const current = draft.version_controls.find(
              (vc) => vc.id === versionControlId
            );
            if (!current) return;

            if (data.template_structure != null)
              current.template_structure = data.template_structure;
            if (data.chat_query != null) current.chat_query = data.chat_query;
            if (data.example_note != null) current.example_note = data.example_note;
          });
        }
      );

      return { previousData };
    },
    onError: (_error, { templateCreationRecordId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<GetTemplateCreationRecordResponse>(
          [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          context.previousData
        );
      }
    },
  });
};
